export default defineI18nConfig(() => ({
  useCookie: false,
  detectBrowserLanguage: false,
  messages: {
    en: {
      offline: {
        title: "You're offline",
        message: 'Some features are unavailable until your connection is back.',
        action_blocked: 'This action requires an internet connection.',
        back_online: "You're back online",
      },
      'space-types': {
        "coworking": "Coworking",
        'den': 'Den',
        'garden': 'Garden',
        'library': 'Library',
        'patio': 'Patio',
        'loft': 'Loft',
        'entrance': 'Entrance',
        'lobby': 'Lobby',
        'kitchen': 'Kitchen',
        'media_room': 'Media Room',
        'multiuse_room': 'Multiuse Room',
        'office': 'Office',
        'pool': 'Pool',
        'hallway': 'Hallway',
        'basement': 'Basement',
        'driveway': 'Driveway',
        'playground': 'Playground',
        'utility_room': 'Utility Room',
        'porch': 'Porch',
        'staircase': 'Staircase',
        'closet': 'Closet',
        'living_room': 'Living Room',
        'bathroom': 'Bathroom',
        'exercise_room': 'Exercise Room',
        'family_room': 'Family Room',
        'sauna': 'Sauna',
        'dining_room': 'Dining Room',
        'reception': 'Reception',
        'facade': 'Facade',
        'balcony': 'Balcony',
        'exterior': 'Exterior',
        'studio': 'studio',
        'bedroom': 'Bedroom',
        'great_room': 'Great Room',
        'garage': 'Garage',
        'pantry': 'Pantry',
        'game_room': 'Game Room',
        'gym': 'Gym',
        'conference_room': 'Conference Room',
        'elevator': 'Elevator',
        'workshop': 'Workshop',
        'laundry': 'Laundry',
        'playroom': 'Playroom'
      },
      property_categories: {
        studio: 'Studio',
        apartment: 'Apartment',
        duplex: 'Duplex',
        triplex: 'Triplex',
        penthouse: 'Penthouse',
        house: 'House',
        bungalow: 'Bungalow',
        villa: 'Villa',
        'hotel-room': 'Hotel',
        'apart-hotel-room': 'Apart-Hotel',
      },
      messages: {
        no_buildings: 'No buildings found.',
        verify_your_identity: 'Verify your identity',
        verification: {
          title: 'Account Verification Required',
          message:
            'To create a booking, you need to verify your identity. This process requires a passport or ID and your mobile phone number.',
        },
        lead: {
          title: 'Your Search is in Motion',
          message:
            'We’ve received your request and are already working on finding the perfect accommodation for you. Our team will conduct a personalized search and share your requirements with our trusted partners (don’t worry – your personal information remains private). This way, you’ll have access to exclusive offers, special discounts, and tailor-made options sent directly by property hosts.\n\nKeep an eye on your inbox – your ideal mid-term rental is on its way! 🌟',
        },
        booking: {
          hint: 'Introduce yourself to the host, including the size of your pet and the ages of your children, if applicable.',
          title: 'Booking Request Sent',
          message: 'Your booking request has been successfully sent to the host.',
          message_visibility: 'Your message will be visible to the host and our team reviewing it. For more information read our',
          personal_details_notice: 'Your personal details will be available to parties required to complete booking.',
        },
        contact: {
          title: 'Your Message Has Been Sent',
          message: 'Thank you for reaching out! The host will respond to you as soon as possible.',
          negotiation_hint: 'Hosts are often willing to negotiate the price for longer stays, tenant profiles, or payment terms.',
          general_hint: 'Avoid sharing sensitive information. Some questions may be added to the FAQ.',
          inquiry_placeholder: 'Write your question here',
          discount_placeholder: 'Write more about you and reasons for discount',
        },
        visit: {
          title: 'Your Visit Request Has Been Sent',
          message: 'Thank you for arranging a visit! The host will respond with date confirmations or next steps soon.',
          hint: 'Most hosts accept in-person visits only for longer stays. For shorter stays, a virtual tour may be an option.',
        },
        confirm_availability: {
          title: 'We will keep you updated!',
          message: 'Our team will try to reach the host of this property and confirm if this apartment is available.',
        },
        request_photos: {
          title: 'Photo session requested!',
          message: 'Our team will reach out to the host and arrange a professional photo session for this property.',
        },
        outdated_photos: {
          title: 'Photos are outdated',
          message: 'This property had outdated photos so we hidden it. If you would like to see updated photos, request a photo session.',
        },
        no_photos: {
          title: 'No photos available',
          message: 'This building has no photos yet. Request a professional photo session to see how it looks.',
        },
        no_description: {
          title: 'No description available',
          message: 'This property has no description. If you need any information, please use the contact form.',
        },
        no_bookmarks: {
          title: 'No bookmarks yet',
          message: 'Tap the heart on a property to save it here.',
        },
        waiting_list: {
          title: 'You have joined the waiting list!',
          message: 'You will be notified about availability updates for this property. You might also receive suggestions for similar properties.',
        },
        investor_account: {
          title: 'Request Sent',
          message: 'Thank you for your interest! We\'ll review your investment criteria and get back to you soon.',
          error: 'There was an error submitting your request. Please try again.',
          submitting: 'Submitting...',
        },
        security_deposit_info: 'Security deposit will be calculated based on risk score assessment and landlord policy.',
        reset_password_email_sent: 'Password reset email has been sent. Please check your inbox.'
      },
      modals: {
        connect_wallet: {
          title: 'Connect wallet',
          description: 'To receive rent using online payments connect to our partners.',
          close_modal: 'Close modal',
          credit_cards: 'Credit Cards',
          paypal_balance: 'Paypal Balance',
          soon: 'Soon!',
          already_connected: 'Already Connected'
        }
      },
      validation: {
        'initialDate': 'Please select the move-in date.'
      },
      auth: {
        'network-request-failed': 'Connection error',
        'invalid-verification-code': 'Invalid verification code',
        'wrong-password': 'Wrong password.',
        'user-not-found': 'User not found',
        'cancelled-popup-request': 'Request cancelled',
        'popup-closed-by-user': 'Popup closed by user',
        'invalid-action-code': 'Invalid or expired link',
        'email-already-in-use': 'E-mail already in use',
        'email-required': 'Please enter your email address',
      },
      errors: {
        404: {
          title: 'Page not found',
          description: 'Sorry, we couldn\'t find the page you\'re looking for. It may have been moved or no longer exists.',
        },
        500: {
          title: 'Something went wrong',
          description: 'We\'re experiencing technical difficulties. Our team has been notified and is working to fix the issue. Please try again in a few moments.',
        },
        generic: {
          title: 'Oops! Something unexpected happened',
          description: 'We encountered an unexpected error. Please try again later.',
        },
        back_to_home: 'Back to Homepage',
      },
      seo: {
        'title': '[name] - Furnished apartments for 1 month+',
        'profile': 'My Profile',
        'find_me_a_flat': 'Find me a flat',
        'for_rent_title': 'Flats for rent in [location] - [country]',
        'for_rent_description': 'Find furnished apartments for rent in [location]. Browse monthly rentals of fully-equipped flats for stays of 1 month or more — flexible mid-term leases, move-in ready and ideal for relocation, remote work or extended trips.',
      },
      testimonials: [
        {
          name: "Jane",
          text: "Excellent service! The platform made it so easy to find a perfect furnished apartment for my mid-term stay. The high-quality materials and virtual tours were incredibly helpful in making my decision."
        },
        {
          name: "Carlos",
          text: "Super professional platform! The advanced search options and detailed property listings made finding a one-month rental a breeze. I loved the virtual tours and drone videos—they really showed the apartments in detail."
        },
        {
          name: "Max",
          text: "Finding a mid-term rental has never been so effortless. The platform's quality listings and innovative features like virtual tours made the process simple and enjoyable. I highly recommend it for anyone looking for a furnished rental."
        },
        {
          name: "Fernando",
          text: "This platform is a game-changer for mid-term rentals. The search functionality is outstanding, and the detailed property information, including drone videos, made finding the perfect one-month apartment incredibly easy."
        }
      ],
      faq: {
        questions: [
          {
            question: "Are pets allowed in the apartments?",
            answer: "Pet policies vary by property. Many of our listings are pet-friendly, but it’s always best to check the specific rules and requirements on the listing page or contact the property owner directly."
          },
          {
            question: "Can I extend my stay?",
            answer: "Absolutely! If you love your furnished apartment and want to extend your stay, simply contact the property owner or manager through our platform. Please note that all extensions must be approved by the host and are subject to availability."
          },
          {
            question: "What is included in the rent?",
            answer: "Every rental includes HOA fees (Homeowners Association) and property taxes. Utilities like electricity, gas, internet, and other services may vary by property. In most cases, these utilities are not included in the base rent. Be sure to check each listing for specific details."
          },
          {
            question: "Are you a real estate agency?",
            answer: "No, we are not an agency. Flat is a marketplace for mid-term rentals. We connect you directly with property owners, apart-hotels, and other providers of furnished apartments, monthly rentals, and flexible accommodation options."
          },
          {
            question: "Do you only work with furnished apartments?",
            answer: "Yes, we exclusively work with furnished apartments and other move-in-ready accommodations. Whether you're looking for a furnished flat, hotel suite, or apart-hotel room, all our properties are fully equipped for your convenience."
          },
          {
            question: "What is the minimum stay for a rental?",
            answer: "We specialize in mid-term rentals with a minimum stay of one month. However, the exact minimum and maximum stay may vary depending on the property. Please check the listing details for specific requirements."
          }
        ]
      },
      head: {
        title: 'Furnished accommodation in [contry] for 1 month+',
      },
      transportation_methods: {
        driving: "Driving",
        walking: "Walking",
        transit: "Public Transport",
      },
      find_flats_for_sale: 'Find Flats for Sale',
      index: {
        header: 'Flats in [location]',
        tagline:
          'Furnished apartments and hotel rooms in [location] for 1 months or more.',
      },
      descriptions: {
        product_description: 'Renting furnished apartments.',
        for_sale:
          'For investors seeking to buy furnished apartments for mid-term rentals, our exclusive membership offers streamlined access to premium listings and valuable market insights.',
        'panama-city':
          'A vibrant metropolis blending modern skyscrapers with historic charm, offering endless cultural experiences and a thriving nightlife.',
        'pacific-coast-beaches':
          'Just outside Panama City, these beaches offer tranquil retreats with stunning ocean views, perfect for relaxation and water sports.',
        'playa-venado':
          'Beach paradise known for its surf-friendly waves, laid-back vibe, and natural beauty, ideal for both adventurers and peace seekers.',
        'boquete':
          'A charming mountain town known for its cool climate, lush landscapes, coffee farms, and vibrant expat community.',
      },
      cities: {
        'pacific-coast-beaches': 'Pacific Coast Beaches',
        panama: 'Panama City',
        coronado: 'Coronado',
        'san-carlos': 'San Carlos',
        'nueva-gorgona': 'Nueva Gorgona',
        chame: 'Punta Chame',
        'punta-chame': 'Punta Chame',
        'las-uvas': 'Las Uvas',
        'playa-coronado': 'Coronado',
        'provincia-de-chiriqui': 'Chiriqui',
        'boquete': 'Boquete',
        'valle-de-anton': 'Valle de Antón',
        'provincia-de-cocle': 'Coclé',
        'provincia-de-veraguas': 'Veraguas',
        veracruz: 'Veracruz',
        'santa-clara': 'Santa Clara',
        'rio-hato': 'Rio Hato',
        'playa-venado': 'Playa Venao',
        'panama-city': 'Panama City',
        pedasi: 'Pedasi',
      },
      space_types: {
        den: 'Den',
        garden: 'Garden',
        library: 'Library',
        patio: 'Patio',
        loft: 'Loft',
        entrance: 'Entrance',
        lobby: 'Lobby',
        kitchen: 'Kitchen',
        media_room: 'Media Room',
        multiuse_room: 'Multiuse Room',
        office: 'Office',
        pool: 'Pool',
        hallway: 'Hallway',
        basement: 'Basement',
        driveway: 'Driveway',
        playground: 'Playground',
        utility_room: 'Utility Room',
        porch: 'Porch',
        staircase: 'Staircase',
        closet: 'Closet',
        living_room: 'Living Room',
        bathroom: 'Bathroom',
        exercise_room: 'Exercise Room',
        family_room: 'Family Room',
        sauna: 'Sauna',
        dining_room: 'Dining Room',
        reception: 'Reception',
        facade: 'Facade',
        balcony: 'Balcony',
        exterior: 'Exterior',
        bedroom: 'Bedroom',
        great_room: 'Great Room',
        garage: 'Garage',
        pantry: 'Pantry',
        game_room: 'Game Room',
        gym: 'Gym',
        conference_room: 'Conference Room',
        elevator: 'Elevator',
        workshop: 'Workshop',
        laundry: 'Laundry',
        playroom: 'Playroom',
      },
      footer: {
        tagline:
          'Furnished apartments and apart-hotels available for monthly rentals.',
      },
      benefits: {
        best_quality_media: {
          title: 'Best quality media',
          description: 'Photos, Videos and 360s.',
        },
        online_bookings: {
          title: 'Online Bookings',
          description: 'Book your unit 100% online.',
        },
        verified_apartments: {
          title: 'Verified apartments',
          description: 'Every unit reviewed in-person.',
        },
        trusted_marketplace: {
          title: 'Trusted marketplace',
          description: 'Over a million nights booked.',
        },
      },
      routes: {
        for_rent: '/for-rent',
        find_me_a_flat: '/find-me-a-flat',
        open_investor_account: '/open-investor-account',
        join_flat: '/join-flat',
        buildings: '/buildings',
      },
      features: {
        c: {
          '24h-reception': '24h Reception',
          'air-condition': 'Air Conditioning',
          'air-conditioning': 'Air Conditioning',
          bar: 'Bar',
          barbeque: 'Barbeque',
          'basketball-court': 'Basketball Court',
          'bed-sheets': 'Bed Sheets',
          'bike-rack': 'Bike Rack',
          breakfast: 'Breakfast',
          'business-center': 'Business Center',
          'cable-tv': 'Cable TV',
          cinema: 'Cinema',
          cleaning: 'Cleaning',
          'cleaning-extra': 'Extra Cleaning',
          'cleaning-included': 'Cleaning Included',
          'dog-park': 'Dog Park',
          elevator: 'Elevator',
          'english-speaking': 'English Speaking',
          'football-court': 'Football Court',
          'game-court': 'Game Court',
          garden: 'Garden',
          'gourmet-lounge': 'Gourmet Lounge',
          'gourmet-space': 'Gourmet Space',
          gym: 'Gym',
          'indoor-swimming-pool': 'Indoor Swimming Pool',
          intercom: 'Intercom',
          internet: 'Internet',
          'internet-in-reception': 'Internet in Reception',
          jacuzzi: 'Jacuzzi',
          laundry: 'Laundry',
          lounge: 'Lounge',
          'meeting-room': 'Meeting Room',
          'outdoor-swimming-pool': 'Outdoor Swimming Pool',
          'outsourced-parking': 'Outsourced Parking',
          parking: 'Parking',
          'parking-extra': 'Extra Parking',
          'parking-included': 'Parking Included',
          'parking-nearby': 'Nearby Parking',
          'party-space': 'Party Space',
          'pet-friendly': 'Pet Friendly',
          'pets-allowed': 'Pets Allowed',
          'pets-not-allowed': 'Pets Not Allowed',
          playground: 'Playground',
          playroom: 'Playroom',
          reception: 'Reception',
          restaurant: 'Restaurant',
          'rooftop-swimming-pool': 'Rooftop Swimming Pool',
          sauna: 'Sauna',
          'spanish-speaking': 'Spanish Speaking',
          steambath: 'Steam Bath',
          'tennis-court': 'Tennis Court',
          towels: 'Towels',
          wifi: 'WiFi',
          'room-service': 'Room Service',
          'co-working': 'Co-working',
          'yoga-studio': 'Yoga Studio',
          'art-gallery': 'Art Gallery',
          atm: 'ATM',
          'fitness-center': 'Fitness Center',
          'sports-court': 'Sports Court',
          'event-space': 'Event Space',
          doorman: 'Doorman',
          lobby: 'Lobby',
          'backup-generator': 'Backup Generator',
          'barbeque-area': 'Barbeque Area',
          'childrens-playroom': "Children's Playroom",
          'squash-court': 'Squash Court',
          'ping-pong': 'Ping Pong',
          'swimming-pool': 'Swimming Pool',
          'games-room': 'Games Room',
          spa: 'Spa',
          library: 'Library',
          garage: 'Garage',
          'billiard-table': 'Billiard Table',
          'steam-room': 'Steam Room',
        },
        p: {
          'air-condition': 'Air Conditioning',
          'air-conditioning': 'Air Conditioning',
          balcony: 'Balcony',
          barbeque: 'Barbeque',
          bathtub: 'Bathtub',
          'bed-sheets': 'Bed Sheets',
          bidet: 'Bidet',
          'cable-tv': 'Cable TV',
          'cleaning': 'Housekeeping',
          'cleaning-extra': 'Extra Cleaning',
          climatization: 'Climatization',
          coffeemaker: 'Coffee Maker',
          cooktop: 'Cooktop',
          couch: 'Couch',
          'dining-table': 'Dining Table',
          'dish-washer': 'Dishwasher',
          dryer: 'Dryer',
          'exhaust-hood': 'Exhaust Hood',
          fridge: 'Fridge',
          furnished: 'Furnished',
          gym: 'Gym',
          heating: 'Heating',
          hood: 'Hood',
          'hot-tub': 'Hot Tub',
          internet: 'Internet',
          iron: 'Iron',
          'iron-board': 'Iron Board',
          kitchen: 'Kitchen',
          'kitchen-island': 'Kitchen Island',
          kitchenette: 'Kitchenette',
          laundry: 'Laundry',
          microwave: 'Microwave',
          'mini-bar': 'Mini Bar',
          'mini-fridge': 'Mini Fridge',
          'no-carpet': 'No Carpet',
          office: 'Office',
          oven: 'Oven',
          'parking-extra': 'Extra Parking',
          'parking-included': 'Parking Included',
          'pets-allowed': 'Pets Allowed',
          'pets-not-allowed': 'Pets Not Allowed',
          shower: 'Shower',
          'smart-tv': 'Smart TV',
          'sofa-bed': 'Sofa Bed',
          stove: 'Stove',
          towels: 'Towels',
          tv: 'TV',
          urban: 'Urban View',
          vault: 'Vault',
          'walk-in-closet': 'Walk-In Closet',
          wardrobe: 'Wardrobe',
          'washing-machine': 'Washing Machine',
          wifi: 'WiFi',
          'working-desk': 'Working Desk',
          'barbeque-area': 'Barbeque Area',
          'vacuum-cleaner': 'Vacuum Cleaner',
          'backup-generator': 'Backup Generator',
          patio: 'Patio',
          'service-quarters': 'Service Quarters',
          sauna: 'Sauna',
          street: 'Street',
          mountain: 'Mountain View',
          park: 'Park View',
          ocean: 'Ocean View',
          workspace: 'Workspace',
          blender: 'Blender',
          toaster: 'Toaster',
          'swimming-pool': 'Swimming Pool',
          parking: 'Parking',
          fan: 'Fan',
          historical: 'Historical',
          gas: 'Gas',
        },
      },
      find_your_dream_flat: 'Find Your Dream Flat',
      let_us_help_you_find_your_perfect_flat:
        'Let us guide you to your perfect flat.',
      access_exclusive_unlisted_offers:
        'Explore private listings not found elsewhere',
      receive_updates_tailored_to_your_preferences:
        'Stay informed with personalized property alerts',
      enjoy_special_discounts_on_select_properties:
        'Benefit from exclusive discounts on curated flats',
      actions: {
        search: 'Search',
        login: 'Login',
        sign_out: 'Logout',
        create_host_account: 'Create Announce',
        create_investor_account: 'Investor Account',
        create_alert: 'Create Alert',
        more_filters: 'More Filters',
        dark_mode: 'Dark mode',
      },
      phrases: {
        can_we_help: 'Can we help?',
      },
      placeholders: {
        property_code: 'Property code (like U1234)',
        search: 'Search by location',
        building_or_community: 'Building or community',
        city: 'City',
        features: 'Features',
        bedrooms: 'Bedrooms',
        studio: 'Studio',
        month: 'month',
        months: 'months',
        monthly_rent: 'Monthly Rent',
        monthly: 'Monthly',
        today: 'Today',
      },
      labels: {
        all: 'All',
        my_bookmarks: 'My Bookmarks',
        save_bookmark: 'Save to bookmarks',
        remove_bookmark: 'Remove from bookmarks',
        loading: 'Loading...',
        show_all: 'Show all ({count})',
        show_less: 'Show less',
        view_details: 'View Details',
        new: 'New',
        floors: 'floors',
        verify_your_identity: 'Verify your identity',
        do_this_later: 'Do this later',
        address: 'Address',
        back_to_search: 'Back to search',
        features_and_conditions: 'Features and Conditions',
        pet: 'Pet',
        kid: 'Kid',
        parking: 'Parking',
        remote_work: 'Remote Work',
        apartment: 'Apartment',
        services: 'Services',
        region: 'Region',
        tell_us_about_rental_needs: 'Tell Us About Your Rental Needs',
        when: 'When?',
        need_asap: 'As soon as possible',
        minimum: 'Minimum',
        maximum: 'Maximum',
        budget: 'Budget',
        minimum_budget: 'Minimum rent',
        maximum_budget: 'Maximum rent',
        minimum_price: 'Minimum price',
        maximum_price: 'Maximum price',
        property_type: 'Property Type',
        property_type_any: 'Any',
        property_type_residential: 'Residential',
        property_type_hotel_outside_pool: 'Hotel/Apart-hotel outside hotel pool',
        property_type_hotel_managed_pool: 'Hotel/Apart-hotel managed by hotel pool',
        target_monthly_return: 'Target Monthly Return',
        target_return_not_sure: 'Not sure',
        target_return_lower_risk: '0.4% - 0.6% (Lower risk)',
        target_return_sweet_spot: '0.6% - 0.8% (Sweet spot)',
        target_return_high_return: '0.8% - 1% (High return)',
        target_return_very_high: '1%+ (Very high return)',
        select_minimum_bedrooms: 'Select Minimum Bedrooms',
        studio_or_larger: 'Studio or larger',
        needs_verification: 'Needs verification',
        no_photos: 'Outdated photos',
        quality_listing_included: 'Quality Listing Included',
        quality_listing_description: 'Our team will create a listing for your property, including a photo session and virtual tour.',
        access_premium_audience: 'Access Premium Audience',
        access_premium_audience_description: 'Our tenants have higher incomes than average.',
        close_deals_without_visits: 'Close Deals Without Visits',
        close_deals_without_visits_description: 'Over 90% of mid-term rentals are completed without visits.',
        announce_your_flat: 'Announce Your Flat',
        announce_description: 'If you have a furnished apartment or hotel room for rent, you can list it on Flat and reach a quality audience of potential guests.',
        landlord: 'Landlord',
        agent: 'Agent',
        apart_hotel: 'Apart-hotel',
        hotel: 'Hotel',
        commission_calculator: 'Commission Calculator',
        net_rent: 'Net Rent',
        gross_rent: 'Gross Rent',
        commission: 'Commission',
        discount_based: 'Discount based',
        commission_base: 'Commission Base',
        commission_payment: 'Commission Payment',
        contract_by: 'Contract by',
        payment_by: 'Payment by',
        host: 'Host',
        platform: 'Platform',
        adding_offer: 'Adding Offer',
        free: 'Free',
        photo_session: 'Photo Session',
        roomcast_access: 'Roomcast access',
        total: 'Total',
        monthly_charge: 'Monthly Charge',
        charged_on_check_in: 'Charged on check-in',
        instant_booking: 'Instant Booking',
        amount: 'Amount',
        people_talking_about_flat: 'People talking about Flat',
        help_and_support: 'Help and Support',
        explore: 'Explore',
        partners: 'Partners',
        legal: 'Legal',
        continue_with_google: 'Continue with Google',
        login_with_apple: 'Log in with Apple',
        signup_with_apple: 'Sign up with Apple',
        or: 'or',
        email: 'Email',
        enter_email: 'Enter your email',
        password: 'Password',
        enter_password: 'Enter your password',
        forgot_password: 'Forgot password?',
        send_password_reminder: 'Send password reminder',
        back_to_signin: 'Back to sign in',
        signin_to_account: 'Sign in to your account',
        no_account: 'Don’t have an account yet?',
        signup_here: 'Sign up here',
        signup_terms_agreement: 'By signing up, you agree to our',
        terms_of_use: 'Terms of Use',
        and: 'and',
        privacy_policy: 'Privacy Policy',
        create_account: 'Create an account',
        already_have_account: 'Already have an account?',
        signin_here: 'Sign in here',
        continue: 'Continue',
        marital_status: 'Marital Status',
        occupation: 'Occupation',
        select_marital_status: 'Select Marital Status',
        single: 'Single',
        married: 'Married',
        divorced: 'Divorced',
        widowed: 'Widowed',
        profession: 'Profession',
        nationality: 'Nationality',
        select_nationality: 'Select Nationality',
        brazilian: 'Brazilian',
        american: 'American',
        canadian: 'Canadian',
        german: 'German',
        french: 'French',
        spanish: 'Spanish',
        other: 'Other',
        send_booking_request: 'Send Booking Request',
        check_in: 'Check-in',
        duration: 'Duration',
        pay_before_check_in: 'Pay before check-in',
        booking_fee: 'Booking Fee',
        monthly_payment: 'Monthly Payment',
        utility_bills: 'Utility Bills',
        check_with_host: 'Check with host',
        require_parking: 'Require Parking',
        staying_with_children: 'Staying with Children',
        staying_with_pet: 'Staying with Pet',
        more_about: 'More about',
        category: 'Category',
        general_inquiry: 'General Inquiry',
        make_offer: 'Make an offer',
        total_rent: 'Total Rent',
        move_in_date: 'Move-in Date',
        move_in_date_placeholder: 'e.g., 2024-06-01 or ASAP',
        message: 'Your Message',
        message_placeholder: 'Type your message here...',
        send_message: 'Send Message',
        arrange_visit: 'Arrange Visit',
        preferred_date: 'Preferred Date',
        preferred_date_placeholder: 'e.g., 2024-05-20 or Next Tuesday',
        mobile_number: 'Your Mobile Number',
        mobile_number_placeholder: 'e.g., +1234567890',
        whatsapp_number: 'WhatsApp Number',
        whatsapp_number_placeholder: 'Enter your WhatsApp number',
        whatsapp_helper_text: 'Only used for notifications and conversations.',
        i_prefer_sms: 'I prefer SMS',
        i_prefer_whatsapp: 'I prefer WhatsApp',
        mobile_helper_text: 'Only used for notifications and conversations.',
        additional_info: 'Additional Information',
        additional_info_placeholder: 'Additional information',
        'available_now': 'Available',
        'from_date': 'From',
        'exit_at': 'From',
        'available': 'Available',
        'available_at_date': 'Available at date',
        'not_available': 'Unavailable',
        join_waiting_list: 'Joing Waiting List',
        find_directions: 'Find direction to ...',
        clear_distances: 'Clear Distances',
        date: 'Date',
        security_deposit: 'Security Deposit',
        find_flats_for_sale: 'Find Flats for Sale',
        locations: 'Locations',
        our_locations: 'Top Rental Destinations',
        flats_for_rent_in: 'Flats for rent in ',
        ask_a_question: 'Ask a Question',
        create_alert: 'Create Alert',
        flats_in: 'Flats in',
        about: 'About',
        faq: 'FAQ',
        find_or_list_flat: 'Find or List a flat',
        rent_my_flat: 'Rent my flat',
        customized_research:
          'Whether you’re a host looking to list your flat or a tenant searching for the perfect place, our team, supported by recommendation AI, will conduct customized research to match you with the ideal rental.',
        sign_up_with_google: 'Sign up with Google',
        first_name: 'First Name',
        last_name: 'Last Name',
        mobile: 'Mobile',
        terms_agreement:
          'By clicking Request Access, you agree to our Terms and Condition, Privacy and Cookies Policy.',
        request_access: 'Request Access',
        login: 'Login',
        open_investor_account: 'Open Investor Account',
        unlock_exclusive_access:
          'Unlock exclusive access to high-value property insights and unique investment opportunities by opening an investor account, available only to pre-qualified individuals and funds.',
        exclusive_content_title: 'Exclusive Content',
        exclusive_content_description:
          'Gain access to financial and visual data curated exclusively for our team and investors, ensuring you stay ahead with the most accurate information.',
        members_only_listings_title: 'Members-Only Listings',
        members_only_listings_description:
          'Be the first to discover incredible deals by accessing our internal list of apartments for sale, giving you a competitive edge in the market.',
        interactive_video_access_title: 'Interactive Video Access',
        interactive_video_access_description:
          'Schedule live video calls with our expert team and access recordings of discussions with landlords and industry leaders.',
        period: 'Period',
        flats_for_rent: 'Flats for Rent',
        flats_for_investment: 'Flats for Investment',
        flats_for_agents: 'For Agents',
        flats_for_landlords: 'For Landlords',
        to: 'to',
        min: 'Min',
        month: 'month',
        months: 'months',
        monthly: 'monthly',
        studio: 'studio',
        bathroom: 'bathroom',
        bathrooms: 'bathrooms',
        bedroom: 'bedroom',
        bedrooms: 'bedrooms',
        inventory: 'Inventory',
        about_property: 'About Property',
        about_building: 'About Building',
        property: 'Property',
        building: 'Building',
        plans: 'Floorplans',
        financial_data: 'Financial data',
        updated_day_ago: 'Updated [days] days ago',
        updated_today: 'Updated today',
        photos: 'More Photos',
        videos: 'Videos',
        spaces: 'Spaces',
        common_areas: 'Common Areas',
        virtual_tour: 'Virtual Tour',
        virtual_tours: 'Virtual Tours',
        location: 'Location',
        distances: 'Distances',
        more_properties_near_location: 'More properties near [location]',
        confirm_availability: 'Confirm Availability',
        request_photos: 'Request Photos',
        booking_request: 'Booking Request',
        how_booking_works: 'How booking works?',
        map: 'Map',
        available_today: 'Available today',
        available_from: 'From ',
        furnished: 'Furnished',
        pets_prohibited: 'Pets prohibited',
        smoking_prohibited: 'Smoking prohibited',
        verified: 'Verified',
        unverified: 'Unverified',
        verify: 'Verify',
        property_tax: 'Propety Tax',
        hoa: 'HOA',
        included: 'Included',
        street_and_number: 'Address (Street and Number)',
        enter_street_number: 'Enter street and number',
        apartment_number: 'Apartment Number',
        optional: 'Optional',
        postal_code: 'Postal Code',
        district: 'District',
        city: 'City',
        state: 'State',
        country: 'Country',
        personal_information: 'Personal Information',
        personal_information_description: 'This information is presented on your public profile, please specify carefully what you want to display.',
        personal_information_hint: 'Your personal information is available to hosts when you create a booking request. It\'s required for verification and contract generation.',
        full_name: 'Full Name',
        birth_date: 'Birth Date',
        document_type: 'Document Type',
        document_number: 'Document Number',
        email_address: 'Email Address',
        sex: 'Sex',
        male: 'Male',
        female: 'Female',
        language: 'Language',
        select_preferred_language: 'Select preferred language',
        english: 'English',
        portuguese: 'Portuguese',
        settings: 'Settings',
        current_address: 'Current Address',
        select_address: 'Select an address',
        preferred_communication_method: 'Preferred Communication Method',
        select_communication_method: 'Select communication method',
        whatsapp: 'WhatsApp',
        update_settings: 'Update Settings',
        settings_hint: 'Update your language preferences and preferred communication method.',
        edit: 'Edit',
        profile: 'Profile',
        profile_photo_hint: 'You can change your profile photo here, you can upload a new photo from your computer.',
        show_information: 'Show information',
        verified_at: 'Verified At',
        created_at: 'Created At',
        not_provided: 'Not provided',
        not_verified: 'Not verified',
        driving_license: 'Driving License',
        id_card: 'ID Card',
        passport: 'Passport',
        connected_wallets: 'Connected Wallets',
        connected_wallets_hint: 'Manage your connected payment wallets.',
        no_wallets_connected: 'No wallets connected yet. Click the button above to connect your wallet.',
      },
      search: {
        property_code: 'By Code',
        locations: 'Locations',
        projects: 'Buildings',
        properties: 'For Rent',
        sales: 'For Sale',
        no_results_title: 'No properties found',
        no_results_message: 'We couldn’t find any properties matching your search. Try adjusting your filters or check back later — new listings are added all the time.',
      },
      countries: {
        brazil: 'Brazil',
        panama: 'Panama',
        united_states: 'United States',
        canada: 'Canada',
        united_kingdom: 'United Kingdom',
        germany: 'Germany',
        france: 'France',
        spain: 'Spain',
        pa: 'Panama',
        br: 'Brazil',
      },
      available_today: 'Flats available today',
      furnished_flats: 'Furnished flats in ',
      buildings_in: 'Buildings in ',
      tabs: {
        properties: 'Properties',
        buildings: 'Buildings',
        mid_term_rentals: 'Mid-term rentals',
        long_term_rentals: 'Long-term rentals',
        investments: 'Investments',
      },
      buttons: {
        view_location: 'View Location',
        find_me_a_flat: 'Find me a flat',
        more_properties: 'View more properties',
        more_buildings: 'View more buildings',
        all_buildings: 'All Buildings',
        contact: 'Contact',
        virtual_visit: 'Virtual Visit',
        visit: 'Visit',
        profile: 'Profile',
      },
    },
    pt: {
      offline: {
        title: 'Você está offline',
        message: 'Alguns recursos ficam indisponíveis até a conexão voltar.',
        action_blocked: 'Esta ação requer uma conexão com a internet.',
        back_online: 'Você está online novamente',
      },
      'space-types': {
        "coworking": "Coworking",
        "den": "Sala Íntima",
        "garden": "Jardim",
        "library": "Biblioteca",
        "patio": "Pátio",
        "loft": "Loft",
        "entrance": "Entrada",
        "lobby": "Saguão",
        "kitchen": "Cozinha",
        "media_room": "Sala de Mídia",
        "multiuse_room": "Sala Multiuso",
        "office": "Escritório",
        "pool": "Piscina",
        "hallway": "Corredor",
        "basement": "Porão",
        "driveway": "Entrada de Veículos",
        "playground": "Parquinho",
        "utility_room": "Área de Serviço",
        "porch": "Varanda",
        "staircase": "Escadaria",
        "closet": "Closet",
        "living_room": "Sala de Estar",
        "bathroom": "Banheiro",
        "exercise_room": "Sala de Ginástica",
        "family_room": "Sala de Família",
        "sauna": "Sauna",
        "dining_room": "Sala de Jantar",
        "reception": "Recepção",
        "facade": "Fachada",
        "balcony": "Sacada",
        "exterior": "Exterior",
        "bedroom": "Quarto",
        "great_room": "Grande Sala",
        "garage": "Garagem",
        "pantry": "Despensa",
        "game_room": "Sala de Jogos",
        "gym": "Academia",
        "conference_room": "Sala de Conferência",
        "elevator": "Elevador",
        "workshop": "Oficina",
        "laundry": "Lavanderia",
        "playroom": "Sala de Brinquedos"
      },
      property_categories: {
        studio: 'Stúdio',
        apartment: 'Apartamento',
        duplex: 'Duplex',
        triplex: 'Triplex',
        penthouse: 'Cobertura',
        house: 'Casa',
        bungalow: 'Bangalô',
        villa: 'Villa',
        'hotel-room': 'Hotel',
        'apart-hotel-room': 'Flat',
      },
      messages: {
        no_buildings: 'Nenhum edifício encontrado.',
        verification: {
          title: 'Verificação de Conta Necessária',
          message:
            'Para fazer uma reserva, você precisa verificar sua identidade. Este processo exige um passaporte ou documento de identidade e seu número de celular.',
        },
        lead: {
          title: 'Sua busca está em andamento',
          message:
            'Recebemos sua solicitação e já estamos trabalhando para encontrar a acomodação perfeita para você. Nossa equipe realizará uma busca personalizada e compartilhará seus requisitos com nossos parceiros confiáveis (não se preocupe – suas informações pessoais permanecem privadas). Dessa forma, você terá acesso a ofertas exclusivas, descontos especiais e opções feitas sob medida, enviadas diretamente pelos anfitriões.\n\nFique de olho na sua caixa de entrada – seu aluguel perfeito de médio prazo está a caminho! 🌟',
        },
        booking: {
          hint: 'Apresente-se ao anfitrião, incluindo o tamanho do seu animal de estimação e as idades de seus filhos, se aplicável.',
          title: 'Solicitação de Reserva Enviada',
          message: 'Sua solicitação de reserva foi enviada com sucesso ao anfitrião.',
          message_visibility: 'Sua mensagem será visível para o anfitrião e nossa equipe que a revisará. Para mais informações leia nossa',
          personal_details_notice: 'Seus dados pessoais estarão disponíveis para as partes necessárias para concluir a reserva.',
        },
        contact: {
          negotiation_hint: 'Os anfitriões geralmente estão dispostos a negociar o preço para estadias mais longas ou condições favoráveis.',
          general_hint: 'Evite compartilhar informações sensíveis. Algumas perguntas podem ser adicionadas à seção de FAQ.',
          title: 'Sua Mensagem Foi Enviada',
          message: 'Obrigado por entrar em contato! O anfitrião responderá o mais breve possível.',
          inquiry_placeholder: 'Escreva sua pergunta aqui',
          discount_placeholder: 'Escreva mais sobre você e os motivos para o desconto',
        },
        visit: {
          title: 'Sua Solicitação de Visita Foi Enviada',
          message: 'Obrigado por agendar uma visita! O anfitrião responderá com as confirmações de data ou próximos passos em breve.',
          hint: 'A maioria dos anfitriões aceita visitas presenciais apenas para estadias mais longas. Para estadias curtas, um tour virtual pode ser uma opção.',
        },
        confirm_availability: {
          title: 'Nós manteremos você atualizado!',
          message: 'Nossa equipe tentará entrar em contato com o anfitrião desta propriedade e confirmar se este apartamento está disponível.',
        },
        request_photos: {
          title: 'Sessão de fotos solicitada!',
          message: 'Nossa equipe entrará em contato com o anfitrião e agendará uma sessão de fotos profissional para este imóvel.',
        },
        outdated_photos: {
          title: 'Fotos desatualizadas',
          message: 'Este imóvel tinha fotos desatualizadas, por isso as ocultamos. Se quiser ver fotos atualizadas, solicite uma sessão de fotos.',
        },
        no_photos: {
          title: 'Sem fotos disponíveis',
          message: 'Este edifício ainda não tem fotos. Solicite uma sessão de fotos profissional para ver como é.',
        },
        no_description: {
          title: 'Sem descrição disponível',
          message: 'Este imóvel não tem descrição. Se precisar de alguma informação, use o formulário de contato.',
        },
        no_bookmarks: {
          title: 'Nenhum favorito ainda',
          message: 'Toque no coração de um imóvel para salvá-lo aqui.',
        },
        waiting_list: {
          title: 'Você entrou na lista de espera!',
          message: 'Você será notificado sobre atualizações de disponibilidade para esta propriedade. Você também poderá receber sugestões de propriedades semelhantes.',
        },
        investor_account: {
          title: 'Solicitação Enviada',
          message: 'Obrigado pelo seu interesse! Analisaremos seus critérios de investimento e entraremos em contato em breve.',
          error: 'Houve um erro ao enviar sua solicitação. Por favor, tente novamente.',
          submitting: 'Enviando...',
        },
        security_deposit_info: 'O depósito de segurança será calculado com base na avaliação do score de risco e política do proprietário.',
        reset_password_email_sent: 'E-mail de redefinição de senha enviado. Por favor, verifique sua caixa de entrada.'
      },
      modals: {
        connect_wallet: {
          title: 'Conectar carteira',
          description: 'Para receber aluguel usando pagamentos online, conecte-se aos nossos parceiros.',
          close_modal: 'Fechar modal',
          credit_cards: 'Cartões de Crédito',
          paypal_balance: 'Saldo Paypal',
          soon: 'Em breve!',
          already_connected: 'Já Conectado'
        }
      },
      validation: {
        'initialDate': 'Selecione a data de entrada.'
      },
      auth: {
        'network-request-failed': 'Erro de conexão',
        'invalid-verification-code': 'Código de verificação inválido',
        'wrong-password': 'Senha incorreta.',
        'user-not-found': 'Usuário não encontrado',
        'cancelled-popup-request': 'Solicitação cancelada',
        'popup-closed-by-user': 'Popup fechado pelo usuário',
        'invalid-action-code': 'Link inválido ou expirado',
        'email-already-in-use': 'E-mail já em uso',
        'email-required': 'Por favor, insira seu endereço de e-mail',
      },
      errors: {
        404: {
          title: 'Página não encontrada',
          description: 'Desculpe, não conseguimos encontrar a página que você está procurando. Ela pode ter sido movida ou não existe mais.',
        },
        500: {
          title: 'Algo deu errado',
          description: 'Estamos enfrentando dificuldades técnicas. Nossa equipe foi notificada e está trabalhando para resolver o problema. Por favor, tente novamente em alguns instantes.',
        },
        generic: {
          title: 'Ops! Algo inesperado aconteceu',
          description: 'Encontramos um erro inesperado. Por favor, tente novamente mais tarde.',
        },
        back_to_home: 'Voltar para a página inicial',
      },
      seo: {
        'title': '[name] - Mais de 20,000 apartamentos mobiliados para alugar por 1 mês+',
        'profile': 'Meu Perfil',
        'find_me_a_flat': 'Encontrar um flat',
        'for_rent_title': 'Flats para alugar em [location] - [country]',
        'for_rent_description': 'Encontre apartamentos mobiliados para alugar em [location]. Veja opções de aluguel mensal de flats totalmente equipados para estadias de 1 mês ou mais — contratos flexíveis de médio prazo, prontos para morar e ideais para mudanças, trabalho remoto ou viagens prolongadas.',
      },
      testimonials: [
        {
          name: "Jane",
          text: "Serviço excelente! A plataforma facilitou muito encontrar um apartamento mobiliado perfeito para minha estadia de médio prazo. Os materiais de alta qualidade e os tours virtuais foram incrivelmente úteis para minha decisão."
        },
        {
          name: "Carlos",
          text: "Plataforma super profissional! As opções avançadas de pesquisa e os anúncios detalhados tornaram a busca por um aluguel de um mês muito simples. Adorei os tours virtuais e os vídeos de drones—eles realmente mostraram os apartamentos em detalhes."
        },
        {
          name: "Max",
          text: "Encontrar uma locação de médio prazo nunca foi tão fácil. Os anúncios de qualidade da plataforma e os recursos inovadores, como tours virtuais, tornaram o processo simples e agradável. Recomendo muito para quem procura um aluguel mobiliado."
        },
        {
          name: "Fernando",
          text: "Esta plataforma é revolucionária para locações de médio prazo. A funcionalidade de busca é incrível, e as informações detalhadas sobre os imóveis, incluindo vídeos de drones, tornaram muito fácil encontrar o apartamento perfeito para um mês."
        }
      ],
      faq: {
        questions: [
          {
            question: "Animais de estimação são permitidos nos apartamentos?",
            answer: "As políticas para animais de estimação variam de acordo com o imóvel. Muitos dos nossos anúncios são pet-friendly, mas é sempre melhor verificar as regras específicas na página do anúncio ou entrar em contato diretamente com o proprietário."
          },
          {
            question: "Posso estender minha estadia?",
            answer: "Com certeza! Se você gostou do apartamento mobiliado e deseja estender sua estadia, basta entrar em contato com o proprietário ou gerente através da nossa plataforma. Por favor, note que todas as extensões precisam ser aprovadas pelo anfitrião e estão sujeitas à disponibilidade."
          },
          {
            question: "O que está incluído no aluguel?",
            answer: "Todo aluguel inclui taxas de condomínio e IPTU. Serviços como eletricidade, gás, internet e outros podem variar dependendo do imóvel. Na maioria dos casos, esses serviços não estão incluídos no valor base do aluguel. Certifique-se de verificar os detalhes de cada anúncio."
          },
          {
            question: "Vocês são uma imobiliária?",
            answer: "Não, nós não somos uma imobiliária. A Flat é um marketplace para aluguéis de médio prazo. Conectamos você diretamente com proprietários, apart-hotéis e outros provedores de apartamentos mobiliados, aluguéis mensais e opções de acomodação flexível."
          },
          {
            question: "Vocês trabalham apenas com apartamentos mobiliados?",
            answer: "Sim, trabalhamos exclusivamente com apartamentos mobiliados e outras acomodações prontas para morar. Seja procurando um flat mobiliado, suíte de hotel ou quarto em apart-hotel, todas as nossas propriedades são totalmente equipadas para sua conveniência."
          },
          {
            question: "Qual é o período mínimo de estadia?",
            answer: "Trabalhamos com aluguéis de médio prazo com estadias mínimas de um mês. No entanto, o período mínimo e máximo pode variar dependendo do imóvel. Verifique os detalhes do anúncio para requisitos específicos."
          }
        ]
      },
      head: {
        title: 'Flat - mais de 20,000 apartamentos mobiliados no [contry]',
      },
      transportation_methods: {
        driving: "Dirigindo",
        walking: "Caminhando",
        transit: "Transporte público",
      },
      locations: 'Localizações',
      descriptions: {
        product_description: 'Aluguel de apartamentos mobiliados.',
        for_sale:
          'Para investidores que buscam comprar apartamentos mobiliados para aluguel de médio prazo, nossa assinatura exclusiva oferece acesso facilitado a listas premium e insights valiosos de mercado.',
        'sao-paulo':
          'O movimentado centro financeiro do Brasil, São Paulo oferece uma mistura dinâmica de cultura, gastronomia e vida noturna em uma paisagem urbana vasta.',
        guarulhos:
          'Localizada próxima a São Paulo, Guarulhos é conhecida pelo seu aeroporto internacional e oferece um estilo de vida mais tranquilo com fácil acesso à cidade.',
        curitiba:
          'Uma cidade verde e ecológica, Curitiba é elogiada por seu planejamento urbano inovador, riqueza cultural e alta qualidade de vida.',
      },
      index: {
        header: 'Flats no [location]',
        tagline:
          'Apartamentos mobiliados e quartos de hotel em [location] por 1 mês ou mais.',
      },
      space_types: {
        den: 'Sala Íntima',
        garden: 'Jardim',
        library: 'Biblioteca',
        patio: 'Pátio',
        loft: 'Loft',
        entrance: 'Entrada',
        lobby: 'Saguão',
        kitchen: 'Cozinha',
        media_room: 'Sala de Mídia',
        multiuse_room: 'Sala Multiuso',
        office: 'Escritório',
        pool: 'Piscina',
        hallway: 'Corredor',
        basement: 'Porão',
        driveway: 'Entrada de Veículos',
        playground: 'Parquinho',
        utility_room: 'Área de Serviço',
        porch: 'Varanda',
        staircase: 'Escadaria',
        closet: 'Closet',
        living_room: 'Sala de Estar',
        bathroom: 'Banheiro',
        exercise_room: 'Sala de Ginástica',
        family_room: 'Sala de Família',
        sauna: 'Sauna',
        dining_room: 'Sala de Jantar',
        reception: 'Recepção',
        facade: 'Fachada',
        balcony: 'Sacada',
        exterior: 'Exterior',
        bedroom: 'Quarto',
        great_room: 'Grande Sala',
        garage: 'Garagem',
        pantry: 'Despensa',
        game_room: 'Sala de Jogos',
        gym: 'Academia',
        conference_room: 'Sala de Conferência',
        elevator: 'Elevador',
        workshop: 'Oficina',
        laundry: 'Lavanderia',
        playroom: 'Sala de Brinquedos',
      },
      footer: {
        tagline:
          'Apartamentos mobiliados e apart-hotéis disponíveis para aluguel mensal.',
      },
      benefits: {
        best_quality_media: {
          title: 'Mídia de melhor qualidade',
          description: 'Fotos, Vídeos e tours virtuais.',
        },
        online_bookings: {
          title: 'Reservas Online',
          description: 'Reserve sua unidade 100% online.',
        },
        verified_apartments: {
          title: 'Apartamentos verificados',
          description: 'Cada unidade revisada pessoalmente.',
        },
        trusted_marketplace: {
          title: 'Marketplace confiável',
          description: 'Mais de um milhão de noites reservadas.',
        },
      },
      routes: {
        for_rent: '/para-alugar',
        find_me_a_flat: '/encontrar-um-flat',
        open_investor_account: '/abrir-conta-de-investidor',
        join_flat: '/anuncie-seu-flat',
        buildings: '/edificios',
      },
      find_your_dream_flat: 'Encontre um ótimo flat',
      let_us_help_you_find_your_perfect_flat:
        'Deixe-nos guiar você até seu apartamento perfeito.',
      access_exclusive_unlisted_offers:
        'Explore ofertas privadas não encontradas em outro lugar',
      receive_updates_tailored_to_your_preferences:
        'Fique informado com alertas de propriedades personalizados',
      enjoy_special_discounts_on_select_properties:
        'Beneficie-se de descontos exclusivos',
      actions: {
        search: 'Buscar',
        login: 'Entrar',
        sign_out: 'Sair',
        create_host_account: 'Criar Anúncio',
        create_investor_account: 'Conta de Investidor',
        create_alert: 'Criar Alerta',
        more_filters: 'Mais Filtros',
        dark_mode: 'Modo escuro',
      },
      phrases: {
        can_we_help: 'Podemos ajudar?',
      },
      placeholders: {
        property_code: 'Código do imóvel (ex. U1234)',
        search: 'Buscar por localização',
        building_or_community: 'Edifício ou comunidade',
        city: 'Cidade',
        features: 'Características',
        bedrooms: 'Dormitórios',
        studio: 'Stúdio',
        month: 'Mês',
        months: 'Meses',
        monthly_rent: 'Aluguel mensal',
        monthly: 'Mensal',
        today: 'Hoje',
      },
      cities: {
        'sao-paulo': 'São Paulo',
        guarulhos: 'Guarulhos',
        osasco: 'Osasco',
        barueri: 'Barueri/Alphaville',
        curitiba: 'Curitiba',
      },
      search: {
        property_code: 'Por Código',
        locations: 'Localizações',
        projects: 'Edifícios',
        properties: 'Para Alugar',
        sales: 'À Venda',
        no_results_title: 'Nenhum imóvel encontrado',
        no_results_message: 'Não encontramos nenhum imóvel correspondente à sua busca. Tente ajustar os filtros ou volte mais tarde — novos anúncios são adicionados o tempo todo.',
      },
      countries: {
        brazil: 'Brasil',
        panama: 'Panamá',
        united_states: 'Estados Unidos',
        canada: 'Canadá',
        united_kingdom: 'Reino Unido',
        germany: 'Alemanha',
        france: 'França',
        spain: 'Espanha',
        pa: 'Panamá',
        br: 'Brasil',
      },
      available_today: 'Apartamentos disponíveis hoje',
      furnished_flats: 'Apartamentos mobiliados no ',
      buildings_in: 'Edifícios em ',
      tabs: {
        mid_term_rentals: 'Aluguéis de médio prazo',
        long_term_rentals: 'Aluguéis de longo prazo',
        investments: 'Investimentos',
        properties: 'Imóveis',
        buildings: 'Edifícios',
      },
      labels: {
        all: 'Todos',
        my_bookmarks: 'Meus Favoritos',
        save_bookmark: 'Salvar nos favoritos',
        remove_bookmark: 'Remover dos favoritos',
        loading: 'Carregando...',
        show_all: 'Ver todos ({count})',
        show_less: 'Ver menos',
        view_details: 'Ver Detalhes',
        new: 'Novo',
        floors: 'andares',
        address: 'Endereço',
        verify_your_identity: 'Verifique sua identidade',
        do_this_later: 'Fazer isso depois',
        back_to_search: 'Voltar à busca',
        features_and_conditions: 'Características e Condições',
        pet: 'Pet',
        kid: 'Criança',
        parking: 'Estacionamento',
        remote_work: 'Trabalho Remoto',
        apartment: 'Apartamento',
        services: 'Serviços',
        region: 'Região',
        tell_us_about_rental_needs: 'Conte-nos sobre suas necessidades',
        when: 'Quando?',
        need_asap: 'Mais rápido possível',
        minimum: 'Mínimo',
        maximum: 'Máximo',
        budget: 'Orçamento',
        minimum_budget: 'Aluguel mínimo',
        maximum_budget: 'Aluguel máximo',
        minimum_price: 'Preço mínimo',
        maximum_price: 'Preço máximo',
        property_type: 'Tipo de Propriedade',
        property_type_any: 'Qualquer',
        property_type_residential: 'Residencial',
        property_type_hotel_outside_pool: 'Hotel/Apart-hotel fora do pool hoteleiro',
        property_type_hotel_managed_pool: 'Hotel/Apart-hotel gerenciado pelo pool hoteleiro',
        target_monthly_return: 'Retorno Mensal Alvo',
        target_return_not_sure: 'Não tenho certeza',
        target_return_lower_risk: '0,4% - 0,6% (Risco menor)',
        target_return_sweet_spot: '0,6% - 0,8% (Ponto ideal)',
        target_return_high_return: '0,8% - 1% (Alto retorno)',
        target_return_very_high: '1%+ (Retorno muito alto)',
        select_minimum_bedrooms: 'Selecione o número mínimo de dormitorios',
        studio_or_larger: 'Studio ou maior',
        needs_verification: 'Precisa verificação',
        no_photos: 'Fotos desatualizadas',
        quality_listing_included: 'Anúncio de Qualidade Incluído',
        quality_listing_description: 'Nossa equipe criará um anúncio para sua propriedade, incluindo sessão de fotos e tour virtual.',
        access_premium_audience: 'Acesso a Público Premium',
        access_premium_audience_description: 'Nossos inquilinos têm rendas mais altas que a média.',
        close_deals_without_visits: 'Feche Negócios Sem Visitas',
        close_deals_without_visits_description: 'Mais de 90% dos aluguéis de médio prazo são concluídos sem visitas.',
        announce_your_flat: 'Anuncie Seu Imóvel',
        announce_description: 'Se você tem um apartamento mobiliado ou quarto de hotel para alugar ou vender, pode anunciá-lo no Flat e alcançar um público qualificado de potenciais hóspedes e investidores.',
        landlord: 'Proprietário',
        agent: 'Corretor',
        apart_hotel: 'Apart-hotel',
        hotel: 'Hotel',
        commission_calculator: 'Calculadora de Comissão',
        net_rent: 'Aluguel Líquido',
        gross_rent: 'Aluguel Bruto',
        commission: 'Comissão',
        discount_based: 'Baseado em desconto',
        commission_base: 'Base de Comissão',
        commission_payment: 'Pagamento de Comissão',
        contract_by: 'Contrato por',
        payment_by: 'Pagamento por',
        host: 'Anfitrião',
        platform: 'Plataforma',
        adding_offer: 'Adicionando Oferta',
        free: 'Grátis',
        photo_session: 'Sessão de Fotos',
        roomcast_access: 'Acesso ao Roomcast',
        total: 'Total',
        monthly_charge: 'Cobrança Mensal',
        charged_on_check_in: 'Cobrado no check-in',
        instant_booking: 'Reserva Instantânea',
        amount: 'Valor',
        people_talking_about_flat: 'Pessoas falando sobre Flat',
        help_and_support: 'Ajuda e Suporte',
        explore: 'Explorar',
        partners: 'Parceiros',
        legal: 'Jurídico',
        continue_with_google: 'Continuar com Google',
        login_with_apple: 'Entrar com o Apple',
        signup_with_apple: 'Registrar-se com o Apple',
        or: 'ou',
        email: 'E-mail',
        enter_email: 'Insira seu e-mail',
        password: 'Senha',
        enter_password: 'Insira sua senha',
        forgot_password: 'Esqueceu a senha?',
        send_password_reminder: 'Enviar lembrete de senha',
        back_to_signin: 'Voltar para login',
        signin_to_account: 'Entrar na sua conta',
        no_account: 'Ainda não tem uma conta?',
        signup_here: 'Registre-se aqui',
        terms_agreement: 'Ao registrar-se, você concorda com nossos',
        terms_of_use: 'Termos de Uso',
        and: 'e',
        privacy_policy: 'Política de Privacidade',
        create_account: 'Criar uma conta',
        already_have_account: 'Já tem uma conta?',
        signin_here: 'Entre aqui',
        continue: 'Continuar',
        marital_status: 'Estado Civil',
        occupation: 'Ocupação',
        select_marital_status: 'Selecione o Estado Civil',
        single: 'Solteiro(a)',
        married: 'Casado(a)',
        divorced: 'Divorciado(a)',
        widowed: 'Viúvo(a)',
        profession: 'Profissão',
        nationality: 'Nacionalidade',
        select_nationality: 'Selecione a Nacionalidade',
        brazilian: 'Brasileiro(a)',
        american: 'Americano(a)',
        canadian: 'Canadense',
        german: 'Alemão/Alemã',
        french: 'Francês/Francesa',
        spanish: 'Espanhol',
        other: 'Outro',
        send_booking_request: 'Enviar Solicitação de Reserva',
        check_in: 'Check-in',
        duration: 'Duração',
        pay_before_check_in: 'Pague antes do check-in',
        booking_fee: 'Taxa de Reserva',
        monthly_payment: 'Pagamento Mensal',
        utility_bills: 'Contas',
        check_with_host: 'Verifique com o anfitrião',
        require_parking: 'Preciso de estacionamento',
        staying_with_children: 'Estou viajando com crianças',
        staying_with_pet: 'Estou viajando com animal de estimação',
        more_about: 'Mais sobre',
        category: 'Categoria da Consulta',
        general_inquiry: 'Consulta Geral',
        make_offer: 'Fazer uma oferta',
        total_rent: 'Aluguel Total',
        move_in_date: 'Data de Entrada',
        move_in_date_placeholder: 'ex.: 2024-06-01 ou Imediato',
        message: 'Sua Mensagem',
        message_placeholder: 'Digite sua mensagem aqui...',
        send_message: 'Enviar Mensagem',
        arrange_visit: 'Agendar Visita',
        locations: 'Localizações',
        preferred_date: 'Data Preferida',
        preferred_date_placeholder: 'ex.: 2024-05-20 ou Próxima Terça-feira',
        mobile_number: 'Seu Número de Celular',
        mobile_number_placeholder: 'ex.: +1234567890',
        whatsapp_number: 'Número do WhatsApp',
        whatsapp_number_placeholder: 'Digite seu número do WhatsApp',
        whatsapp_helper_text: 'Usado apenas para notificações e conversas.',
        i_prefer_sms: 'Prefiro SMS',
        i_prefer_whatsapp: 'Prefiro WhatsApp',
        mobile_helper_text: 'Usado apenas para notificações e conversas.',
        additional_info: 'Informações Adicionais',
        additional_info_placeholder: 'Informações adicionais',
        'available_now': 'Disponível',
        'from_date': 'A partir',
        'exit_at': 'Saída',
        'available': 'Disponível',
        'available_at_date': 'Disponível a partir da',
        'not_available': 'Indisponível',
        join_waiting_list: 'Lista de espera',
        min: 'Mín',
        find_directions: 'Encontre a direção para',
        clear_distances: 'Limpar distâncias',
        date: 'Data',
        security_deposit: 'Depósito de Segurança',
        find_flats_for_sale: 'Encontre Apartamentos à Venda',
        our_locations: 'Melhores Destinos de Aluguel',
        flats_for_rent_in: 'Flats para alugar em ',
        ask_a_question: 'Faça uma pergunta',
        create_alert: 'Criar Alerta',
        flats_in: 'Apartamentos em',
        about: 'Sobre',
        faq: 'Perguntas frequentes',
        find_or_list_flat: 'Encontre ou Anuncie Flat',
        rent_my_flat: 'Anuncie meu flat',
        customized_research:
          'Se você está procurando um flat, nosso sistema de IA irá combiná-lo com opções adequadas e alertá-lo sobre novos anúncios. Proprietários de imóveis também podem anunciar seus espaços aqui para acessar um novo mercado de locatários.',
        sign_up_with_google: 'Inscrever-se com Google',
        first_name: 'Nome',
        last_name: 'Sobrenome',
        mobile: 'Celular',
        signup_terms_agreement:
          'Ao clicar em Solicitar Acesso, você concorda com nossos .',
        request_access: 'Solicitar Acesso',
        login: 'Entrar',
        open_investor_account: 'Abrir Conta de Investidor',
        unlock_exclusive_access:
          'Desbloqueie o acesso exclusivo a insights de propriedades de alto valor e oportunidades de investimento únicas, abrindo uma conta de investidor, disponível apenas para indivíduos e fundos pré-qualificados.',
        exclusive_content_title: 'Conteúdo Exclusivo',
        exclusive_content_description:
          'Tenha acesso a dados financeiros e visuais curados exclusivamente para nossa equipe e investidores, garantindo que você esteja sempre à frente com as informações mais precisas.',
        members_only_listings_title: 'Listagens Exclusivas para Membros',
        members_only_listings_description:
          'Seja o primeiro a descobrir ofertas incríveis acessando nossa lista interna de apartamentos à venda, dando a você uma vantagem competitiva no mercado.',
        interactive_video_access_title: 'Acesso a Vídeos Interativos',
        interactive_video_access_description:
          'Agende videochamadas ao vivo com nossa equipe de especialistas e acesse gravações de discussões com proprietários e líderes da indústria.',
        period: 'Período',
        flats_for_rent: 'Flats para Alugar',
        flats_for_investment: 'Flats para Venda',
        flats_for_agents: 'Para Corretores',
        flats_for_landlords: 'Para Proprietários',
        to: 'até',
        month: 'mês',
        months: 'meses',
        monthly: 'mensal',
        studio: 'studio',
        bathroom: 'banheiro',
        bathrooms: 'banheiros',
        bedroom: 'Dormitorio',
        bedrooms: 'Dormitorios',
        inventory: 'Inventário',
        about_property: 'Sobre o Imóvel',
        about_building: 'Sobre o Edifício',
        property: 'Imóvel',
        building: 'Edifício',
        plans: 'Plantas',
        financial_data: 'Dados Financeiros',
        updated_day_ago: 'Atualizado há [days] dias',
        updated_today: 'Atualizado hoje',
        photos: 'Fotos',
        videos: 'Vídeos',
        spaces: 'Espaços',
        common_areas: 'Área Social',
        virtual_tour: 'Tour Virtual',
        virtual_tours: 'Tours Virtuais',
        location: 'Localização',
        distances: 'Distâncias',
        more_properties_near_location: 'Mais imóveis próximos a [location]',
        confirm_availability: 'Confirmar Disponibilidade',
        request_photos: 'Solicitar Fotos',
        booking_request: 'Solicitação de Reserva',
        how_booking_works: 'Como funciona a reserva?',
        map: 'Mapa',
        available_today: 'Disponível hoje',
        available_from: 'Disponível a partir de ',
        furnished: 'Mobiliado',
        pets_prohibited: 'Pets proibidos',
        smoking_prohibited: 'Proibido fumar',
        verified: 'Verificado',
        unverified: 'Não verificado',
        verify: 'Verificar',
        property_tax: 'IPTU',
        hoa: 'Condomínio',
        included: 'Incluído',
        street_and_number: 'Endereço (Rua e Número)',
        enter_street_number: 'Digite a rua e número',
        apartment_number: 'Número do Apartamento',
        optional: 'Opcional',
        postal_code: 'CEP',
        district: 'Bairro',
        city: 'Cidade',
        state: 'Estado',
        country: 'País',
        personal_information: 'Informações Pessoais',
        personal_information_description: 'Essas informações são apresentadas no seu perfil público, especifique cuidadosamente o que deseja exibir.',
        personal_information_hint: 'Suas informações pessoais ficam disponíveis para os anfitriões quando você cria uma solicitação de reserva. São necessárias para verificação e geração de contrato.',
        full_name: 'Nome Completo',
        birth_date: 'Data de Nascimento',
        document_type: 'Tipo de Documento',
        document_number: 'Número do Documento',
        email_address: 'Endereço de Email',
        sex: 'Sexo',
        male: 'Masculino',
        female: 'Feminino',
        language: 'Idioma',
        select_preferred_language: 'Selecione o idioma preferido',
        english: 'Inglês',
        portuguese: 'Português',
        settings: 'Configurações',
        current_address: 'Endereço Atual',
        select_address: 'Selecione um endereço',
        preferred_communication_method: 'Método de Comunicação Preferido',
        select_communication_method: 'Selecione o método de comunicação',
        whatsapp: 'WhatsApp',
        update_settings: 'Atualizar Configurações',
        settings_hint: 'Atualize suas preferências de idioma e método de comunicação preferido.',
        edit: 'Editar',
        profile: 'Perfil',
        profile_photo_hint: 'Você pode alterar sua foto de perfil aqui, você pode enviar uma nova foto do seu computador.',
        show_information: 'Mostrar informações',
        verified_at: 'Verificado Em',
        created_at: 'Criado Em',
        not_provided: 'Não informado',
        not_verified: 'Não verificado',
        driving_license: 'Carteira de Motorista',
        id_card: 'Carteira de Identidade',
        passport: 'Passaporte',
        connected_wallets: 'Carteiras Conectadas',
        connected_wallets_hint: 'Gerencie suas carteiras de pagamento conectadas.',
        no_wallets_connected: 'Nenhuma carteira conectada ainda. Clique no botão acima para conectar sua carteira.',
      },
      features: {
        c: {
          '24h-reception': 'Recepção 24h',
          'air-condition': 'Ar Condicionado',
          bar: 'Bar',
          barbeque: 'Churrasqueira',
          'basketball-court': 'Quadra de Basquete',
          'bed-sheets': 'Lençóis Fornecidos',
          'bike-rack': 'Bicicletário',
          breakfast: 'Café da Manhã Disponível',
          'business-center': 'Centro de Negócios',
          'cable-tv': 'TV a Cabo',
          cinema: 'Cinema',
          cleaning: 'Serviço de Limpeza',
          'cleaning-extra': 'Serviço de Limpeza Extra',
          'cleaning-included': 'Limpeza Incluída',
          'dog-park': 'Área para Cães',
          elevator: 'Elevador',
          'english-speaking': 'Atendimento em Inglês',
          'football-court': 'Quadra de Futebol',
          'game-court': 'Quadra de Jogos',
          garden: 'Jardim',
          'gourmet-lounge': 'Lounge Gourmet',
          'gourmet-space': 'Espaço Gourmet',
          gym: 'Academia',
          'indoor-swimming-pool': 'Piscina Coberta',
          intercom: 'Interfone',
          internet: 'Acesso à Internet',
          'internet-in-reception': 'Internet na Recepção',
          jacuzzi: 'Jacuzzi',
          laundry: 'Lavanderia',
          lounge: 'Lounge',
          'meeting-room': 'Sala de Reuniões',
          'outdoor-swimming-pool': 'Piscina ao Ar Livre',
          'outsourced-parking': 'Estacionamento Terceirizado',
          parking: 'Vaga de garagem',
          'parking-extra': 'Estacionamento Extra',
          'parking-included': 'Estacionamento Incluído',
          'parking-nearby': 'Estacionamento Próximo',
          'party-space': 'Espaço para Festas',
          'pet-friendly': 'Aceita Pets',
          'pets-allowed': 'Permite Pets',
          'pets-not-allowed': 'Não Permite Pets',
          playground: 'Parquinho',
          playroom: 'Sala de Jogos',
          reception: 'Recepção',
          restaurant: 'Restaurante',
          'rooftop-swimming-pool': 'Piscina na Cobertura',
          sauna: 'Sauna',
          'spanish-speaking': 'Atendimento em Espanhol',
          steambath: 'Banho Turco',
          'tennis-court': 'Quadra de Tênis',
          towels: 'Toalhas Fornecidas',
          wifi: 'Wi-Fi',
          'event-space': 'Espaço para Eventos',
          doorman: 'Porteiro',
          lobby: 'Lobby',
          'backup-generator': 'Gerador de Emergência',
          'barbeque-area': 'Área de Churrasco',
          'childrens-playroom': 'Brinquedoteca',
          'squash-court': 'Quadra de Squash',
          'ping-pong': 'Mesa de Ping-Pong',
          'swimming-pool': 'Piscina',
          'games-room': 'Sala de Jogos',
          spa: 'Spa',
          library: 'Biblioteca',
          'garage': 'Garagem',
          'billiard-table': 'Mesa de Sinuca',
          'steam-room': 'Sala de Vapor',
          'room-service': 'Serviço de Quarto',
          'co-working': 'Espaço de Coworking',
          'yoga-studio': 'Estúdio de Yoga',
          'art-gallery': 'Galeria de Arte',
          atm: 'Caixa Eletrônico',
          'fitness-center': 'Centro de Fitness',
          'sports-court': 'Quadra de Esportes',
        },
        p: {
          'air-condition': 'Ar Condicionado',
          'air-conditioning': 'Ar Condicionado',
          balcony: 'Varanda',
          barbeque: 'Churrasqueira',
          bathtub: 'Banheira',
          'bed-sheets': 'Lençóis',
          bidet: 'Bidê',
          'cable-tv': 'TV a Cabo',
          'cleaning': 'Serviço de Limpeza',
          'cleaning-extra': 'Limpeza Extra',
          climatization: 'Climatização',
          coffeemaker: 'Cafeteira',
          cooktop: 'Cooktop',
          couch: 'Sofá',
          'dining-table': 'Mesa de Jantar',
          'dish-washer': 'Lava-Louças',
          dryer: 'Secadora',
          'exhaust-hood': 'Coifa',
          fridge: 'Geladeira',
          furnished: 'Mobiliado',
          heating: 'Aquecimento',
          hood: 'Capuz',
          'hot-tub': 'Banheira de Hidromassagem',
          internet: 'Internet',
          iron: 'Ferro de Passar',
          'iron-board': 'Tábua de Passar',
          kitchen: 'Cozinha',
          'kitchen-island': 'Ilha na Cozinha',
          kitchennete: 'Cozinha Compacta',
          laundry: 'Lavanderia',
          microwave: 'Micro-ondas',
          'mini-bar': 'Mini Bar',
          'mini-fridge': 'Frigobar',
          'no-carpet': 'Sem Carpete',
          office: 'Escritório',
          oven: 'Forno',
          'parking-extra': 'Estacionamento Extra',
          'parking-included': 'Estacionamento Incluído',
          'pets-allowed': 'Permite Pets',
          'pets-not-allowed': 'Não Permite Pets',
          shower: 'Chuveiro',
          'smart-tv': 'Smart TV',
          'sofa-bed': 'Sofá-Cama',
          stove: 'Fogão',
          towels: 'Toalhas',
          tv: 'TV',
          urban: 'Vista para Cidade',
          vault: 'Cofre',
          'walk-in-closet': 'Closet',
          wardrobe: 'Guarda-Roupas',
          'washing-machine': 'Máquina de Lavar',
          wifi: 'Wi-Fi',
          'working-desk': 'Mesa de Trabalho',
          ocean: 'Vista para o Mar',
          workspace: 'Espaço de Trabalho',
          blender: 'Liquidificador',
          toaster: 'Torradeira',
          'swimming-pool': 'Piscina',
          parking: 'Estacionamento',
          fan: 'Ventilador',
          historical: 'Histórico',
          gas: 'Gás Encanado',
          kitchenette: 'Cozinha Compacta',
          mountain: 'Vista para a Montanha',
          'barbeque-area': 'Área de Churrasco',
          'vacuum-cleaner': 'Aspirador de Pó',
          'backup-generator': 'Gerador de Emergência',
          patio: 'Pátio',
          'service-quarters': 'Quarto de Serviço',
          street: 'Vista para a Rua',
          park: 'Vista para o Parque',
        },
      },
      buttons: {
        view_location: 'Ver Localização',
        find_me_a_flat: 'Encontrar um flat',
        more_properties: 'Ver mais propriedades',
        more_buildings: 'Ver mais edifícios',
        all_buildings: 'Todos os Edifícios',
        contact: 'Contato',
        virtual_visit: 'Visita Virtual',
        visit: 'Visita',
        profile: 'Perfil',
      },
    },
  },
}));
